import type { IframeScenario, ScenarioSettings } from "../settings/iframes-mapping";
import { GovernPriority } from "owa-client-types/lib/GovernPriority";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not messing with useState wrapper in shared/
import { useEffect, useRef, useState } from "react";
import { lazyGovern } from "owa-tti";
import { trace } from "owa-trace";
import { setIframeInfo } from "../utils/getIframeInfo";

export type IframeState = "UNMOUNTED" | "PRELOAD-HIDDEN" | "HIDDEN" | "VISIBLE";

/**
 * Manages the state of the iframe
 * IF PRELOADING IS ENABLED (preloadingEnabled is true):
 *   1. Set the state as "UNMOUNTED". The iframe should be rendered as `null`. Governor schedules preloading by delaying the component mount.
 *   2. When the governor is ready, update the state to "PRELOAD-HIDDEN". The iframe is ready to render.
 *   3. On user interaction (isVisible becomes true), switch the state to "VISIBLE". This should take precedence over step 1 and 2.
 *   4. For the rest of time the component is mounted, the state should be "VISIBLE" or "HIDDEN" based on user interaction (based on isVisible).
 * IF PRELOADING IS DISABLED (preloadingEnabled is false):
 *   1. Set the state as "VISIBLE" or "HIDDEN" (based on isVisible). The iframe should always be rendered.
 *   2. On user interaction, switch the state between "VISIBLE" or "HIDDEN" (based on isVisible).
 */
export function useIframeState(scenario: IframeScenario, isVisible: boolean, settings: ScenarioSettings, dpLoadStart: () => void): {
    value: IframeState;
    shouldRender: boolean;
    isVisibleToUser: boolean;
} {
    const { enablePreloading: preloadingEnabled, enableCaching: cachingEnabled, governTask, } = settings;
    // Always start the state as unmounted
    const state = useRef<IframeState>("UNMOUNTED");
    const isPreloading = useRef<boolean>(preloadingEnabled);
    // If not preloading, set the component as mounted to load right away
    const [isMounted, setIsMounted] = useState<boolean>(!preloadingEnabled);
    
    useEffect(() => {
        // Only schedule a preload when: preloading is enabled, iframe not yet user-visible, and not already mounted
        if (preloadingEnabled && !isVisible && !isMounted) {
            lazyGovern.importAndExecute({
                ...governTask,
                task: () => {
                    if (!isMounted) {
                        trace.info(` Scheduler will preload the iframe for scenario: ${scenario}`, "iframeWrapper");
                        // eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- trigger mount to flip from null to iframe
                        setIsMounted(true);
                        dpLoadStart();
                    }
                },
                priority: GovernPriority.Default,
            });
        }
    }, [scenario, preloadingEnabled, governTask, isVisible, isMounted]);
    
    if (isVisible) {
        // ALWAYS set the the state to visible if the component is set to visible
        state.current = "VISIBLE";
        isPreloading.current = false;
        
        // If the iframe is not mounted, set it to mounted to force it to render
        if (!isMounted) {
            setIsMounted(true);
            dpLoadStart();
        }
    }
    else if (isMounted) {
        state.current = isPreloading.current ? "PRELOAD-HIDDEN" : "HIDDEN";
    }
    
    const renderIfCached = isMounted && cachingEnabled;
    
    trace.info(` Iframe state: ${state.current}, isMounted: ${isMounted}, renderIfCached: ${renderIfCached}, cachingEnabled: ${cachingEnabled}`, "iframeWrapper");
    
    const results = {
        value: state.current,
        shouldRender: renderIfCached || state.current == "VISIBLE",
        isVisibleToUser: state.current === "VISIBLE",
    };
    
    setIframeInfo(scenario, "currentState", results.value);
    setIframeInfo(scenario, "isRendered", results.shouldRender);
    
    return results;
}
